// AccountPage.jsx (полный файл)
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import api from "../../../shared/api/apiClient";
import styles from "./AccountPage.module.css";

import { safeStr, parseDate, formatActivityTime, actionIcon, activityTags } from "./accountHelpers";

const PASSWORD_MASK = "********";

const decodeJwtPayload = (token) => {
    if (!token || typeof token !== "string") return null;
    const parts = token.split(".");
    if (parts.length < 2) return null;

    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    try {
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split("")
                .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
                .join("")
        );
        return JSON.parse(jsonPayload);
    } catch {
        return null;
    }
};

function AccountPage() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    const roleLabel = (role) => {
        const n = Number(role);
        if (n === 1) return t("account_role_admin");
        if (n === 2) return t("account_role_manager");
        if (n === 3) return t("account_role_employee");
        return role ? String(role) : "-";
    };

    const initialsFromName = (fullName, email) => {
        const base = String(fullName || "").trim();
        if (base) {
            const parts = base.split(/\s+/).filter(Boolean);
            const a = (parts[0] || "").charAt(0).toUpperCase();
            const b = (parts[1] || "").charAt(0).toUpperCase();
            return (a + b) || a || "U";
        }
        const em = String(email || "").trim();
        if (em) return em.slice(0, 2).toUpperCase();
        return "U";
    };

    const normalizeMode = (v) => String(v || "").trim().toLowerCase();

    const getEmailModeLabel = (mode) => {
        const m = normalizeMode(mode);
        if (m === "corporate") return t("account_email_mode_corporate");
        if (m === "employee") return t("account_email_mode_personal");
        if (m === "hybrid") return t("account_email_mode_hybrid");
        return t("account_email_mode_unknown");
    };

    const getEmailModeHint = (mode) => {
        const m = normalizeMode(mode);
        if (m === "hybrid") return t("account_email_mode_hint_hybrid");
        if (m === "corporate") return t("account_email_mode_hint_corporate");
        if (m === "employee") return t("account_email_mode_hint_personal");
        return t("account_email_mode_hint_controlled");
    };

    const getEmailModeBadgeTone = (mode) => {
        const m = normalizeMode(mode);
        if (m === "hybrid") return "primary";
        if (m === "corporate") return "neutral";
        if (m === "employee") return "info";
        return "neutral";
    };

    const extractCrmEmailMode = (data) => {
        const sj = data?.settings_json || data?.settings || data || {};
        const emailBlock = sj?.email || sj?.crm_email || {};
        return { mode: emailBlock?.mode ?? "hybrid" };
    };

    // --- session ---
    const actorToken = useMemo(() => localStorage.getItem("token") || "", []);
    const jwtPayload = useMemo(() => decodeJwtPayload(actorToken), [actorToken]);

    const actorEmailLS = useMemo(() => {
        return localStorage.getItem("email") || jwtPayload?.email || jwtPayload?.sub_email || "";
    }, [jwtPayload]);

    const actorRoleLS = useMemo(() => {
        return localStorage.getItem("role") || jwtPayload?.role || "";
    }, [jwtPayload]);

    const actorUserIdLS = useMemo(() => {
        return localStorage.getItem("user_id") || String(jwtPayload?.user_id ?? jwtPayload?.id ?? "") || "";
    }, [jwtPayload]);

    const actorTenantIdLS = useMemo(() => {
        return localStorage.getItem("tenant_id") || String(jwtPayload?.tenant_id ?? "") || "";
    }, [jwtPayload]);

    const updatedAtLS = useMemo(() => {
        return String(jwtPayload?.updated_at ?? localStorage.getItem("updated_at") ?? "");
    }, [jwtPayload]);

    const actorFullNameLS = useMemo(() => {
        return (
            localStorage.getItem("full_name") ||
            jwtPayload?.full_name ||
            jwtPayload?.name ||
            localStorage.getItem("fullName") ||
            ""
        );
    }, [jwtPayload]);

    // --- tabs ---
    const [tab, setTab] = useState("profile"); // profile | email | password

    // left inner tabs like reference: Deal Info | Activities
    const [leftTab, setLeftTab] = useState("activities"); // deal | activities

    // --- CRM email mode ---
    const [crmEmail, setCrmEmail] = useState({
        loading: false,
        mode: "hybrid",
        error: "",
    });

    const [telegramLink, setTelegramLink] = useState({
        loading: false,
        error: "",
        success: "",
        data: null,
        code: "",
        instruction: "",
        expires_at: "",
        telegram_deep_link: "",
        bot_username: "",
    });

    const fetchTelegramLink = async () => {
        setTelegramLink((p) => ({ ...p, loading: true, error: "" }));
        try {
            const { data } = await api.get("/auth/telegram/internal/my-link");
            setTelegramLink((p) => ({ ...p, loading: false, data, error: "" }));
        } catch (e) {
            setTelegramLink((p) => ({
                ...p,
                loading: false,
                error: e?.response?.data?.detail || "Failed to load Telegram link status",
            }));
        }
    };

    const generateTelegramLinkCode = async () => {
        setTelegramLink((p) => ({ ...p, loading: true, error: "", success: "" }));
        try {
            const { data } = await api.post("/auth/telegram/internal/link-code");
            setTelegramLink((p) => ({
                ...p,
                loading: false,
                code: data?.code || "",
                instruction: data?.instruction || "",
                expires_at: data?.expires_at || "",
                telegram_deep_link: data?.telegram_deep_link || "",
                bot_username: data?.bot_username || "",
                success: data?.telegram_deep_link
                    ? "Telegram link is ready. Open Telegram and press Start."
                    : "Telegram link code generated.",
                error: "",
            }));
        } catch (e) {
            setTelegramLink((p) => ({
                ...p,
                loading: false,
                error: e?.response?.data?.detail || "Failed to generate Telegram link code",
            }));
        }
    };

    const unlinkTelegramAccount = async () => {
        if (!window.confirm(t("account_unlink_telegram_confirm"))) return;
        setTelegramLink((p) => ({ ...p, loading: true, error: "", success: "" }));
        try {
            await api.delete("/auth/telegram/internal/my-link");
            setTelegramLink((p) => ({
                ...p,
                loading: false,
                data: { linked: false },
                code: "",
                instruction: "",
                expires_at: "",
                telegram_deep_link: "",
                bot_username: "",
                success: "Telegram account unlinked.",
            }));
        } catch (e) {
            setTelegramLink((p) => ({
                ...p,
                loading: false,
                error: e?.response?.data?.detail || "Failed to unlink Telegram account",
            }));
        }
    };

    const fetchCrmEmailMode = async () => {
        setCrmEmail((p) => ({ ...p, loading: true, error: "" }));
        try {
            const { data } = await api.get("/auth/crmSettings");
            const extracted = extractCrmEmailMode(data);
            setCrmEmail((p) => ({ ...p, loading: false, mode: extracted?.mode ?? "hybrid", error: "" }));
        } catch (e) {
            console.error("Failed to load CRM email mode:", e);
            setCrmEmail((p) => ({
                ...p,
                loading: false,
                mode: "hybrid",
                error: e?.response?.data?.detail || t("account_err_failed_load_crm_email_settings"),
            }));
        }
    };

    // hide Email tab when corporate
    const hideEmailTab = normalizeMode(crmEmail.mode) === "corporate";

    useEffect(() => {
        if (hideEmailTab && tab === "email") setTab("profile");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hideEmailTab]);

    // --- activity feed ---
    const [activity, setActivity] = useState({
        loading: false,
        error: "",
        items: [],
        mode: "actor",
    });

    // ✅ NEW: controls state (limit + date range)
    const [activityUi, setActivityUi] = useState({
        limit: 20,
        date_from: "",
        date_to: "",
    });

    const fetchActivityFeed = async ({ mode = "actor", limit = 20, skip = 0, date_from = "", date_to = "" } = {}) => {
        const uid = Number(actorUserIdLS);
        if (!uid) return;

        setActivity((p) => ({ ...p, loading: true, error: "" }));

        // 1) prefer: /auth/activityLog/me
        try {
            const { data } = await api.get("/auth/activityLog/me", {
                params: {
                    mode,
                    limit,
                    skip,
                    // optional:
                    date_from: date_from || undefined,
                    date_to: date_to || undefined,
                },
            });

            const list = Array.isArray(data) ? data : data?.items || [];
            setActivity((p) => ({
                ...p,
                loading: false,
                error: "",
                items: Array.isArray(list) ? list : [],
                mode,
            }));
            return;
        } catch (e) {
            const status = e?.response?.status;
            if (status && status !== 404) {
                setActivity((p) => ({
                    ...p,
                    loading: false,
                    error: e?.response?.data?.detail || t("account_err_failed_load_activity") || "Failed to load activity",
                }));
                return;
            }
        }

        // 2) fallback: /auth/activityLog/all -> filter by emploee_id
        try {
            const { data } = await api.get("/auth/activityLog/all", { params: { limit: 200, skip: 0 } });
            const all = Array.isArray(data) ? data : [];
            const filtered =
                mode === "target"
                    ? all.filter((x) => Number(x?.user_id) === uid)
                    : all.filter((x) => Number(x?.emploee_id) === uid);

            // client-side date range fallback
            const df = date_from ? new Date(`${date_from}T00:00:00`) : null;
            const dt = date_to ? new Date(`${date_to}T23:59:59.999`) : null;

            const dateFiltered = filtered.filter((x) => {
                const d = parseDate(x?.created_at);
                if (!d) return true;
                if (df && d < df) return false;
                if (dt && d > dt) return false;
                return true;
            });

            setActivity((p) => ({
                ...p,
                loading: false,
                error: "",
                items: dateFiltered.slice(skip, skip + limit),
                mode,
            }));
        } catch (e) {
            setActivity((p) => ({
                ...p,
                loading: false,
                error: e?.response?.data?.detail || t("account_err_failed_load_activity") || "Failed to load activity",
            }));
        }
    };

    // --- user profile (/auth/user/me) ---
    const [me, setMe] = useState({
        loading: false,
        error: "",
        data: null,
    });

    const fetchMe = async () => {
        setMe((p) => ({ ...p, loading: true, error: "" }));
        try {
            const { data } = await api.get("/auth/user/me");
            setMe({ loading: false, error: "", data });

            // лёгкая синхронизация (не обязательна, но помогает остальной части UI)
            if (data?.email) localStorage.setItem("email", data.email);
            if (data?.userId != null) localStorage.setItem("user_id", String(data.userId));
            if (data?.updated_at) localStorage.setItem("updated_at", String(data.updated_at));
            const full = `${data?.first_name || ""} ${data?.last_name || ""}`.trim();
            if (full) localStorage.setItem("full_name", full);
            if (data?.phone) localStorage.setItem("phone", String(data.phone));
        } catch (e) {
            console.error("Failed to load /auth/user/me", e);
            setMe((p) => ({
                ...p,
                loading: false,
                error: e?.response?.data?.detail || t("account_err_failed_load_profile") || "Failed to load profile",
            }));
        }
    };

    // profile editing
    const [isEditingProfile, setIsEditingProfile] = useState(false);
    const [profileSaving, setProfileSaving] = useState(false);
    const [profileError, setProfileError] = useState("");
    const [profileSuccess, setProfileSuccess] = useState("");

    const [profileForm, setProfileForm] = useState({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        country: "",
        city: "",
        date_of_birth: "", // yyyy-mm-dd
        description: "",
    });

    const toISODate = (d) => {
        if (!d) return "";
        // backend может отдать date "2026-02-19" или ISO datetime
        const s = String(d);
        if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
        const dt = parseDate(s);
        if (!dt) return "";
        const y = dt.getFullYear();
        const m = String(dt.getMonth() + 1).padStart(2, "0");
        const da = String(dt.getDate()).padStart(2, "0");
        return `${y}-${m}-${da}`;
    };

    const hydrateProfileFormFromMe = (data) => {
        const d = data || {};
        setProfileForm({
            first_name: d?.first_name || "",
            last_name: d?.last_name || "",
            email: d?.email || actorEmailLS || "",
            phone: d?.phone || localStorage.getItem("phone") || "",
            country: d?.country || "",
            city: d?.city || "",
            date_of_birth: toISODate(d?.date_of_birth),
            description: d?.description || "",
        });
    };

    const onStartEditProfile = () => {
        setProfileError("");
        setProfileSuccess("");
        hydrateProfileFormFromMe(me.data);
        setIsEditingProfile(true);
    };

    const onCancelEditProfile = () => {
        setProfileError("");
        setProfileSuccess("");
        hydrateProfileFormFromMe(me.data);
        setIsEditingProfile(false);
    };

    const buildProfilePatch = () => {
        // отправляем только то, что реально можно менять
        const payload = {
            first_name: String(profileForm.first_name || "").trim() || null,
            last_name: String(profileForm.last_name || "").trim() || null,
            email: String(profileForm.email || "").trim() || null,
            phone: String(profileForm.phone || "").trim() || null,
            country: String(profileForm.country || "").trim() || null,
            city: String(profileForm.city || "").trim() || null,
            date_of_birth: profileForm.date_of_birth ? profileForm.date_of_birth : null,
            description: String(profileForm.description || "").trim() || null,
        };

        // exclude null
        Object.keys(payload).forEach((k) => payload[k] == null && delete payload[k]);
        return payload;
    };

    const onSaveProfile = async () => {
        const uid = Number(me?.data?.userId || actorUserIdLS);
        if (!uid) return;

        setProfileSaving(true);
        setProfileError("");
        setProfileSuccess("");

        try {
            const payload = buildProfilePatch();
            await api.put(`/auth/user/edit/${uid}`, payload);

            setProfileSuccess(t("saved") || "Saved");
            setTimeout(() => setProfileSuccess(""), 2200);

            await fetchMe();
            setIsEditingProfile(false);
        } catch (e) {
            console.error("Failed to save profile", e);
            setProfileError(e?.response?.data?.detail || t("account_err_failed_save_profile") || "Failed to save profile");
        } finally {
            setProfileSaving(false);
        }
    };

    // --- email account ---
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [serverAccount, setServerAccount] = useState(null);
    const [notConfigured, setNotConfigured] = useState(false);

    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const [form, setForm] = useState({
        provider: "smtp",
        email_address: "",
        is_active: true,
        smtp_json: {
            host: "",
            port: 587,
            user: "",
            password: "",
            tls: true,
            from_name: "",
        },
        oauth_json: null,
    });

    // --- password ---
    const [pwdSaving, setPwdSaving] = useState(false);
    const [pwdError, setPwdError] = useState("");
    const [pwdSuccess, setPwdSuccess] = useState("");
    const [pwdForm, setPwdForm] = useState({
        current_password: "",
        new_password: "",
        confirm_password: "",
    });

    const [pwdKey, setPwdKey] = useState(0);
    const pwdWasClearedOnceRef = useRef(false);

    const clearPasswordFields = () => {
        setPwdForm({ current_password: "", new_password: "", confirm_password: "" });
    };

    const showSuccess = (msg) => {
        setSuccess(msg);
        setTimeout(() => setSuccess(""), 2500);
    };

    const showPwdSuccess = (msg) => {
        setPwdSuccess(msg);
        setTimeout(() => setPwdSuccess(""), 2500);
    };

    const normalizeFromServer = (obj) => {
        const smtp = (obj?.smtp_json && typeof obj.smtp_json === "object" ? obj.smtp_json : {}) || {};
        return {
            provider: obj?.provider || "smtp",
            email_address: obj?.email_address || "",
            is_active: typeof obj?.is_active === "boolean" ? obj.is_active : true,
            smtp_json: {
                host: smtp.host || "",
                port: smtp.port ?? 587,
                user: smtp.user || "",
                password: smtp.password || "",
                tls: typeof smtp.tls === "boolean" ? smtp.tls : true,
                from_name: smtp.from_name || "",
            },
            oauth_json: obj?.oauth_json || null,
        };
    };

    const fetchMyEmailAccount = async () => {
        try {
            setLoading(true);
            setError("");
            setSuccess("");

            const { data } = await api.get("/auth/emailAccount");
            if (data?.detail === "not_configured") {
                setNotConfigured(true);
                setServerAccount(null);
                setForm((prev) => ({
                    ...prev,
                    email_address: prev.email_address || actorEmailLS || "",
                }));
                return;
            }

            setNotConfigured(false);
            setServerAccount(data);
            setForm(normalizeFromServer(data));
        } catch (e) {
            console.error(e);
            setError(e?.response?.data?.detail || t("account_err_failed_load_account_settings"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMyEmailAccount();
        fetchCrmEmailMode();

        // keep your current behavior: initial activities fetch
        fetchActivityFeed({ mode: "actor", limit: 20, skip: 0 });

        fetchMe();
        fetchTelegramLink();

        if (!pwdWasClearedOnceRef.current) {
            pwdWasClearedOnceRef.current = true;
            clearPasswordFields();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (tab === "password") {
            setPwdKey((k) => k + 1);
            clearPasswordFields();
            setPwdError("");
            setPwdSuccess("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    // keep profile form in sync when we load me (only if not in editing)
    useEffect(() => {
        if (!isEditingProfile && me?.data) {
            hydrateProfileFormFromMe(me.data);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [me.data]);

    const onChangeTop = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
    const onChangeSmtp = (field, value) =>
        setForm((prev) => ({ ...prev, smtp_json: { ...(prev.smtp_json || {}), [field]: value } }));

    const validateEmailAccount = () => {
        const email = String(form.email_address || "").trim();
        if (!email) return t("email_required") || "Email is required";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return t("invalid_email") || "Invalid email format";

        if ((form.provider || "smtp") === "smtp") {
            const host = String(form.smtp_json?.host || "").trim();
            const user = String(form.smtp_json?.user || "").trim();
            const portRaw = form.smtp_json?.port;

            if (!host) return t("account_err_smtp_host_required");
            if (!user) return t("account_err_smtp_user_required");

            const port = Number(portRaw);
            if (!port || port <= 0) return t("account_err_smtp_port_positive");

            const pwd = String(form.smtp_json?.password || "");
            const hadAccount = !!serverAccount && !notConfigured;
            if (!hadAccount) {
                if (!pwd || pwd === PASSWORD_MASK) return t("account_err_smtp_password_required");
            }
        }
        return "";
    };

    const buildEmailAccountPayload = () => ({
        provider: form.provider || "smtp",
        email_address: String(form.email_address || "").trim(),
        is_active: !!form.is_active,
        smtp_json: {
            host: String(form.smtp_json?.host || "").trim(),
            port: Number(form.smtp_json?.port || 587),
            user: String(form.smtp_json?.user || "").trim(),
            password: form.smtp_json?.password || "",
            tls: !!form.smtp_json?.tls,
            from_name: String(form.smtp_json?.from_name || "").trim(),
        },
        oauth_json: form.oauth_json || null,
    });

    const onSaveEmailAccount = async () => {
        const msg = validateEmailAccount();
        if (msg) {
            setError(msg);
            return;
        }
        try {
            setSaving(true);
            setError("");
            setSuccess("");

            const payload = buildEmailAccountPayload();
            const { data } = await api.put("/auth/emailAccount", payload);

            setServerAccount(data);
            setNotConfigured(false);
            setForm(normalizeFromServer(data));
            showSuccess(t("saved") || "Saved");
        } catch (e) {
            console.error(e);
            setError(e?.response?.data?.detail || t("account_err_failed_save_settings"));
        } finally {
            setSaving(false);
        }
    };

    const onToggleEmailAccountActive = async () => {
        try {
            setSaving(true);
            setError("");
            setSuccess("");

            const payload = buildEmailAccountPayload();
            payload.is_active = !form.is_active;

            const { data } = await api.put("/auth/emailAccount", payload);

            setServerAccount(data);
            setNotConfigured(false);
            setForm(normalizeFromServer(data));
            showSuccess(t("account_msg_updated"));
        } catch (e) {
            console.error(e);
            setError(e?.response?.data?.detail || t("account_err_failed_update"));
        } finally {
            setSaving(false);
        }
    };

    const validatePassword = () => {
        const np = String(pwdForm.new_password || "");
        const cp = String(pwdForm.confirm_password || "");
        if (!np) return t("account_err_new_password_required");
        if (np.length < 8) return t("account_err_password_min_8");
        if (np !== cp) return t("account_err_passwords_no_match");
        return "";
    };

    const tryChangePassword = async (payload) => {
        try {
            await api.post("/auth/change-password", payload);
            return true;
        } catch (e) {
            if (e?.response?.status && e.response.status !== 404) throw e;
        }

        try {
            await api.put("/auth/password", payload);
            return true;
        } catch (e) {
            if (e?.response?.status && e.response.status !== 404) throw e;
        }

        throw new Error(t("account_err_no_password_endpoint"));
    };

    const onUpdatePassword = async () => {
        const msg = validatePassword();
        if (msg) {
            setPwdError(msg);
            return;
        }

        try {
            setPwdSaving(true);
            setPwdError("");
            setPwdSuccess("");

            const payload = {
                current_password: String(pwdForm.current_password || ""),
                new_password: String(pwdForm.new_password || ""),
            };

            await tryChangePassword(payload);

            clearPasswordFields();
            setPwdKey((k) => k + 1);

            showPwdSuccess(t("account_msg_password_updated"));
        } catch (e) {
            console.error(e);
            setPwdError(e?.response?.data?.detail || e?.message || t("account_err_failed_update"));
        } finally {
            setPwdSaving(false);
        }
    };

    const emailAccountStatusLabel = form.is_active ? t("account_status_active") : t("account_status_inactive");

    const crmModeLabel = crmEmail.loading ? t("account_email_mode_loading") : getEmailModeLabel(crmEmail.mode);
    const crmModeHint = crmEmail.loading ? t("account_email_mode_reading") : getEmailModeHint(crmEmail.mode);
    const crmModeTone = getEmailModeBadgeTone(crmEmail.mode);

    const locale = (i18n?.language || "en").toLowerCase().startsWith("ru") ? "ru" : "en";

    // effective actor values (prefer /me)
    const actorEmail = me?.data?.email || actorEmailLS;
    const actorUserId = String(me?.data?.userId ?? actorUserIdLS ?? "");
    const actorTenantId = String(actorTenantIdLS || "");
    const actorRole = me?.data?.role || actorRoleLS;

    const actorFullName = useMemo(() => {
        const fn = me?.data?.first_name || "";
        const ln = me?.data?.last_name || "";
        const full = `${fn} ${ln}`.trim();
        return full || actorFullNameLS || actorEmail || t("account_user_fallback");
    }, [me?.data?.first_name, me?.data?.last_name, actorFullNameLS, actorEmail, t]);

    const updatedAt = String(me?.data?.updated_at ?? updatedAtLS ?? "");

    const avatarInitials = useMemo(() => initialsFromName(actorFullName, actorEmail), [actorFullName, actorEmail]);

    // UI actions
    const onMail = () => {
        if (!actorEmail) return;
        window.location.href = `mailto:${actorEmail}`;
    };

    const onCall = () => {
        const phone = me?.data?.phone || localStorage.getItem("phone") || "";
        if (!phone) return;
        window.location.href = `tel:${phone}`;
    };

    const onMore = () => {
        if (hideEmailTab) {
            setTab("password");
            return;
        }
        setTab("email");
    };

    // avatar click placeholder (future upload)
    const onAvatarClick = () => {
        console.log("avatar click (future upload)");
    };

    // last activity (best effort)
    const lastActivityText = useMemo(() => {
        const first = (activity.items || [])[0];
        const created = parseDate(first?.created_at);
        if (!created) return "";
        return formatActivityTime(created, locale);
    }, [activity.items, locale]);

    // profile computed display (while editing show live full name)
    const liveFullName = useMemo(() => {
        const f = String(profileForm.first_name || "").trim();
        const l = String(profileForm.last_name || "").trim();
        const full = `${f} ${l}`.trim();
        return full || actorFullName;
    }, [profileForm.first_name, profileForm.last_name, actorFullName]);

    const telegramManualCommand = useMemo(() => {
        return telegramLink?.code ? `/link ${telegramLink.code}` : "";
    }, [telegramLink?.code]);

    const telegramExpiresText = useMemo(() => {
        if (!telegramLink?.expires_at) return "";
        const dt = parseDate(telegramLink.expires_at) || new Date(telegramLink.expires_at);
        if (!dt || Number.isNaN(dt.getTime())) return String(telegramLink.expires_at);
        return dt.toLocaleString();
    }, [telegramLink?.expires_at]);

    const copyTelegramCommand = async () => {
        if (!telegramManualCommand) return;
        try {
            await navigator.clipboard.writeText(telegramManualCommand);
            setTelegramLink((p) => ({ ...p, success: t("account_telegram_command_copied") }));
        } catch {
            setTelegramLink((p) => ({ ...p, error: t("account_telegram_command_copy_failed") }));
        }
    };

    const copyTelegramCode = async () => {
        if (!telegramLink?.code) return;
        try {
            await navigator.clipboard.writeText(telegramLink.code);
            setTelegramLink((p) => ({ ...p, success: t("account_telegram_code_copied") }));
        } catch {
            setTelegramLink((p) => ({ ...p, error: t("account_telegram_code_copy_failed") }));
        }
    };

    // ✅ apply/clear for activity filters
    const applyActivityFilters = () => {
        fetchActivityFeed({
            mode: "actor",
            limit: Number(activityUi.limit || 20),
            skip: 0,
            date_from: activityUi.date_from || "",
            date_to: activityUi.date_to || "",
        });
    };

    const clearActivityFilters = () => {
        const next = { limit: 20, date_from: "", date_to: "" };
        setActivityUi(next);
        fetchActivityFeed({ mode: "actor", limit: 20, skip: 0 });
    };

    return (
        <div className={styles.page}>
            <div className={styles.shell}>
                {/* LEFT MONOLITH */}
                <aside className={styles.profilePane}>
                    <div className={styles.leftCard}>
                        <div className={styles.leftTopGradient} />
                        <div className={styles.leftInner}>
                            <div className={styles.avatarWrap}>
                                <button
                                    type="button"
                                    className={styles.avatarBtn}
                                    onClick={onAvatarClick}
                                    title={t("account_change_avatar") || "Change avatar"}
                                >
                                    <div className={styles.avatarCircle}>{avatarInitials}</div>
                                </button>
                            </div>

                            <div className={styles.leftName}>{actorFullName}</div>
                            <div className={styles.leftRole}>{roleLabel(actorRole)}</div>

                            <div className={styles.topActionsRow}>
                                <button type="button" onClick={onMail} className={`${styles.actionBtn} ${styles.actionBtnMail}`} title={t("account_mail")}>
                                    <i class="lni lni-envelope-1"></i>
                                </button>
                                <button
                                    type="button"
                                    onClick={onCall}
                                    className={`${styles.actionBtn} ${styles.actionBtnCall}`}
                                    title={t("call")}
                                    disabled={!((me?.data?.phone || localStorage.getItem("phone") || "").trim())}
                                >
                                    <i class="lni lni-telephone-1"></i>
                                </button>
                                <button type="button" onClick={onMore} className={`${styles.actionBtn} ${styles.actionBtnMore}`} title={t("account_more")}>
                                    <i class="lni lni-menu-meatballs-1"></i>
                                </button>
                            </div>

                            <div className={styles.lastActivityRow}>
                                <span className={styles.lastActivityDot} />
                                <span className={styles.lastActivityText}>
                                    {t("account_last_activity") || "Last Activity"}: {lastActivityText || "—"}
                                </span>
                            </div>

                            {/* inner tabs like reference */}
                            <div className={styles.innerTabsRow}>
                                <button
                                    type="button"
                                    className={`${styles.innerTabBtn} ${leftTab === "activities" ? styles.innerTabActive : ""}`}
                                    onClick={() => setLeftTab("activities")}
                                >
                                    {t("account_activities") || "Activities"}
                                </button>
                                <button
                                    type="button"
                                    className={`${styles.innerTabBtn} ${leftTab === "deal" ? styles.innerTabActive : ""}`}
                                    onClick={() => setLeftTab("deal")}
                                >
                                    {t("account_deal_info") || "Deal Info"}
                                </button>
                            </div>

                            {leftTab === "deal" ? (
                                <div className={styles.dealPlaceholder}>
                                    {t("account_deal_info_placeholder") || "Deal info will appear here (placeholder)."}
                                </div>
                            ) : (
                                <>
                                    <div className={styles.activityCard}>
                                        <div className={styles.activityHeader}>
                                            <div className={styles.activityHeaderTitle}>
                                                <select
                                                    className={styles.activitySelect}
                                                    value={activityUi.limit}
                                                    onChange={(e) => setActivityUi((p) => ({ ...p, limit: Number(e.target.value) }))}
                                                    disabled={activity.loading}
                                                >
                                                    <option value={10}>10</option>
                                                    <option value={20}>20</option>
                                                    <option value={30}>30</option>
                                                    <option value={50}>50</option>
                                                </select>
                                            </div>

                                            {/* ✅ NEW: grouped actions to avoid huge gaps */}
                                            <div className={styles.activityHeaderActions}>
                                                <button
                                                    type="button"
                                                    className={styles.activityRefreshBtn}
                                                    onClick={applyActivityFilters}
                                                    disabled={activity.loading}
                                                    title={t("apply") || "Apply"}
                                                >
                                                    <i class="lni lni-search-text"></i>
                                                </button>

                                                <button
                                                    type="button"
                                                    className={styles.activityRefreshBtn}
                                                    onClick={clearActivityFilters}
                                                    disabled={activity.loading}
                                                    title={t("clear") || "Clear"}
                                                >
                                                    <i class="lni lni-eraser-1"></i>
                                                </button>

                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        fetchActivityFeed({
                                                            mode: "actor",
                                                            limit: Number(activityUi.limit || 20),
                                                            skip: 0,
                                                            date_from: activityUi.date_from || "",
                                                            date_to: activityUi.date_to || "",
                                                        })
                                                    }
                                                    disabled={activity.loading}
                                                    className={styles.activityRefreshBtn}
                                                    title={t("refresh") || "Refresh"}
                                                >
                                                    <i class="lni lni-refresh-circle-1-clockwise"></i>
                                                </button>
                                            </div>
                                        </div>

                                        {/* ✅ controls */}
                                        <div className={styles.activityControls}>
                                            <div className={styles.activityControlRow}>                                    
                                                <input
                                                    className={styles.activityDateInput}
                                                    type="date"
                                                    value={activityUi.date_from}
                                                    onChange={(e) => setActivityUi((p) => ({ ...p, date_from: e.target.value }))}
                                                    disabled={activity.loading}
                                                    title={t("from") || "From"}
                                                />
                                                <input
                                                    className={styles.activityDateInput}
                                                    type="date"
                                                    value={activityUi.date_to}
                                                    onChange={(e) => setActivityUi((p) => ({ ...p, date_to: e.target.value }))}
                                                    disabled={activity.loading}
                                                    title={t("to") || "To"}
                                                />
                                            </div>
                                        </div>

                                        {activity.error ? (
                                            <div className={styles.activityError}>{activity.error}</div>
                                        ) : (
                                            <div className={styles.activityList}>
                                                {(!activity.items || activity.items.length === 0) && !activity.loading ? (
                                                    <div className={styles.activityEmpty}>{t("account_no_activity") || "No activity yet"}</div>
                                                ) : (
                                                    (activity.items || []).map((it) => {
                                                        const created = parseDate(it?.created_at);
                                                        const whenText = formatActivityTime(created, locale);

                                                        const entity = safeStr(it?.entity).replace(/_/g, " ");
                                                        const action = safeStr(it?.action).toLowerCase();

                                                        const title =
                                                            safeStr(it?.description) ||
                                                            (entity ? `${entity} ${action}` : safeStr(it?.action) || "activity");

                                                        const tags = activityTags(it?.action);
                                                        const desc = safeStr(it?.description) ? (entity ? entity : "") : "";

                                                        return (
                                                            <div key={it?.id ?? `${it?.created_at}-${title}`} className={styles.activityItem}>
                                                                <div className={styles.activityRail}>
                                                                    <div className={styles.activityDot} title={safeStr(it?.action)}>
                                                                        {actionIcon(it?.action)}
                                                                    </div>
                                                                </div>

                                                                <div className={styles.activityText}>
                                                                    <div className={styles.activityTitle} title={title}>
                                                                        {title}
                                                                    </div>

                                                                    <div className={styles.activityTime}>{whenText}</div>

                                                                    {(tags.length > 0 || desc) && (
                                                                        <div className={styles.activityMetaRow}>
                                                                            {tags.map((tg, idx) => (
                                                                                <span
                                                                                    key={`${tg.label}-${idx}`}
                                                                                    className={`${styles.activityTag} ${
                                                                                        tg.tone === "accepted"
                                                                                            ? styles.tagAccepted
                                                                                            : tg.tone === "new"
                                                                                            ? styles.tagNew
                                                                                            : styles.tagProgress
                                                                                    }`}
                                                                                >
                                                                                    {t(tg.label)}
                                                                                </span>
                                                                            ))}

                                                                            {desc ? (
                                                                                <div className={styles.activityDesc} title={desc}>
                                                                                    {desc}
                                                                                </div>
                                                                            ) : null}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <div className={styles.sectionTitle}>{t("account_section_email_sending")}</div>
                                    <div className={styles.emailModeBox}>
                                        <div className={styles.emailModeTop}>
                                            <span className={styles.emailModeLabel}>{t("account_crm_mode")}</span>
                                            <span className={`${styles.badge} ${styles[`badge_${crmModeTone}`]}`}>{crmModeLabel}</span>
                                        </div>
                                        <div className={styles.emailModeHint}>{crmModeHint}</div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </aside>

                {/* RIGHT CONTENT */}
                <main className={styles.contentPane}>
                    <div className={styles.breadcrumb}>
                        <span>{t("account_breadcrumb_settings")}</span>
                        <span className={styles.sep}>›</span>
                        <span className={styles.breadStrong}>
                            {tab === "profile"
                                ? t("account_tab_personal_details")
                                : tab === "email"
                                ? t("account_tab_email_settings")
                                : t("account_tab_password")}
                        </span>
                    </div>

                    {/* right-side tabs */}
                    <div className={styles.rightTabsRow}>
                        <button
                            type="button"
                            className={`${styles.rightTabBtn} ${tab === "profile" ? styles.rightTabActive : ""}`}
                            onClick={() => setTab("profile")}
                        >
                            {t("account_tab_personal_details")}
                        </button>

                        {!hideEmailTab && (
                            <button
                                type="button"
                                className={`${styles.rightTabBtn} ${tab === "email" ? styles.rightTabActive : ""}`}
                                onClick={() => setTab("email")}
                            >
                                {t("account_tab_email_settings")}
                            </button>
                        )}

                        <button
                            type="button"
                            className={`${styles.rightTabBtn} ${tab === "password" ? styles.rightTabActive : ""}`}
                            onClick={() => setTab("password")}
                        >
                            {t("account_tab_password")}
                        </button>
                    </div>

                    {success && <div className="alert alert-success">{success}</div>}
                    {error && <div className="alert alert-danger">{error}</div>}

                    {/* PROFILE TAB */}
                    {tab === "profile" && (
                        <div className={styles.rightCard}>
                            <div className={styles.rightHeader}>
                                <div>
                                    <div className={styles.rightTitle}>{t("account_personal_info_title")}</div>
                                    <div className={styles.rightSub}>
                                        {isEditingProfile ? t("account_profile_editing") || "Editing mode" : t("account_personal_info_sub")}
                                    </div>
                                </div>

                                <div className={styles.profileHeaderActions}>
                                    {isEditingProfile ? (
                                        <>
                                            <button
                                                type="button"
                                                className={styles.secondaryBtnPill}
                                                onClick={onCancelEditProfile}
                                                disabled={profileSaving}
                                            >
                                                {t("cancel") || "Cancel"}
                                            </button>
                                            <button
                                                type="button"
                                                className={styles.primaryBtnPill}
                                                onClick={onSaveProfile}
                                                disabled={profileSaving}
                                                title={t("save") || "Save"}
                                            >
                                                {profileSaving ? t("account_saving") : t("account_save")}
                                            </button>
                                        </>
                                    ) : (
                                        <button
                                            type="button"
                                            className={styles.secondaryBtnPill}
                                            onClick={onStartEditProfile}
                                            title={t("account_edit") || "Edit"}
                                        >
                                            {t("account_edit")}
                                        </button>
                                    )}
                                </div>
                            </div>

                            {(profileSuccess || profileError) && (
                                <div className={styles.profileAlerts}>
                                    {profileSuccess && <div className="alert alert-success">{profileSuccess}</div>}
                                    {profileError && <div className="alert alert-danger">{profileError}</div>}
                                </div>
                            )}

                            <div className={styles.formGrid}>
                                {/* FULL NAME */}
                                <div className={styles.formItem}>
                                    <label>{t("account_full_name")}</label>
                                    <input className={styles.readOnlyInput} value={liveFullName || ""} readOnly placeholder={t("n_a")} />
                                </div>

                                {/* ROLE */}
                                <div className={styles.formItem}>
                                    <label>{t("role") || "Role"}</label>
                                    <input className={styles.readOnlyInput} value={roleLabel(actorRole)} readOnly />
                                </div>

                                {/* First name */}
                                <div className={styles.formItem}>
                                    <label className={isEditingProfile ? styles.editableLabel : ""}>
                                        {t("account_first_name") || "First name"}
                                    </label>
                                    <input
                                        className={isEditingProfile ? styles.editableInput : styles.readOnlyInput}
                                        value={isEditingProfile ? profileForm.first_name : me?.data?.first_name || ""}
                                        readOnly={!isEditingProfile}
                                        onChange={(e) => setProfileForm((p) => ({ ...p, first_name: e.target.value }))}
                                        placeholder={t("n_a")}
                                    />
                                </div>

                                {/* Last name */}
                                <div className={styles.formItem}>
                                    <label className={isEditingProfile ? styles.editableLabel : ""}>
                                        {t("account_last_name") || "Last name"}
                                    </label>
                                    <input
                                        className={isEditingProfile ? styles.editableInput : styles.readOnlyInput}
                                        value={isEditingProfile ? profileForm.last_name : me?.data?.last_name || ""}
                                        readOnly={!isEditingProfile}
                                        onChange={(e) => setProfileForm((p) => ({ ...p, last_name: e.target.value }))}
                                        placeholder={t("n_a")}
                                    />
                                </div>

                                {/* Email */}
                                <div className={styles.formItem}>
                                    <label className={isEditingProfile ? styles.editableLabel : ""}>{t("account_email")}</label>
                                    <input
                                        className={isEditingProfile ? styles.editableInput : styles.readOnlyInput}
                                        value={isEditingProfile ? profileForm.email : actorEmail || ""}
                                        readOnly={!isEditingProfile}
                                        onChange={(e) => setProfileForm((p) => ({ ...p, email: e.target.value }))}
                                        placeholder={t("n_a")}
                                    />
                                </div>

                                {/* User ID */}
                                <div className={styles.formItem}>
                                    <label>{t("account_user_id")}</label>
                                    <input className={styles.readOnlyInput} value={actorUserId || ""} readOnly placeholder={t("n_a")} />
                                </div>

                                {/* Phone */}
                                <div className={styles.formItem}>
                                    <label className={isEditingProfile ? styles.editableLabel : ""}>
                                        {t("account_phone") || "Phone"}
                                    </label>
                                    <input
                                        className={isEditingProfile ? styles.editableInput : styles.readOnlyInput}
                                        value={isEditingProfile ? profileForm.phone : me?.data?.phone || ""}
                                        readOnly={!isEditingProfile}
                                        onChange={(e) => setProfileForm((p) => ({ ...p, phone: e.target.value }))}
                                        placeholder={t("n_a")}
                                    />
                                </div>

                                {/* Tenant */}
                                <div className={styles.formItem}>
                                    <label>{t("account_tenant")}</label>
                                    <input className={styles.readOnlyInput} value={actorTenantId || ""} readOnly placeholder={t("n_a")} />
                                </div>

                                {/* Country */}
                                <div className={styles.formItem}>
                                    <label className={isEditingProfile ? styles.editableLabel : ""}>
                                        {t("account_country") || "Country"}
                                    </label>
                                    <input
                                        className={isEditingProfile ? styles.editableInput : styles.readOnlyInput}
                                        value={isEditingProfile ? profileForm.country : me?.data?.country || ""}
                                        readOnly={!isEditingProfile}
                                        onChange={(e) => setProfileForm((p) => ({ ...p, country: e.target.value }))}
                                        placeholder={t("n_a")}
                                    />
                                </div>

                                {/* City */}
                                <div className={styles.formItem}>
                                    <label className={isEditingProfile ? styles.editableLabel : ""}>{t("account_city") || "City"}</label>
                                    <input
                                        className={isEditingProfile ? styles.editableInput : styles.readOnlyInput}
                                        value={isEditingProfile ? profileForm.city : me?.data?.city || ""}
                                        readOnly={!isEditingProfile}
                                        onChange={(e) => setProfileForm((p) => ({ ...p, city: e.target.value }))}
                                        placeholder={t("n_a")}
                                    />
                                </div>

                                {/* Date of birth */}
                                <div className={styles.formItem}>
                                    <label className={isEditingProfile ? styles.editableLabel : ""}>
                                        {t("account_date_of_birth") || "Date of birth"}
                                    </label>
                                    <input
                                        className={isEditingProfile ? styles.editableInput : styles.readOnlyInput}
                                        type={isEditingProfile ? "date" : "text"}
                                        value={isEditingProfile ? profileForm.date_of_birth : toISODate(me?.data?.date_of_birth) || ""}
                                        readOnly={!isEditingProfile}
                                        onChange={(e) => setProfileForm((p) => ({ ...p, date_of_birth: e.target.value }))}
                                        placeholder={t("n_a")}
                                    />
                                </div>

                                {/* Updated at */}
                                <div className={styles.formItem}>
                                    <label>{t("account_updated_at")}</label>
                                    <input className={styles.readOnlyInput} value={updatedAt || ""} readOnly placeholder={t("n_a")} />
                                </div>

                                {/* Description */}
                                <div className={`${styles.formItem} ${styles.formItemWide}`}>
                                    <label className={isEditingProfile ? styles.editableLabel : ""}>
                                        {t("account_description") || "Description"}
                                    </label>
                                    <textarea
                                        className={isEditingProfile ? styles.editableTextarea : styles.readOnlyTextarea}
                                        value={isEditingProfile ? profileForm.description : me?.data?.description || ""}
                                        readOnly={!isEditingProfile}
                                        onChange={(e) => setProfileForm((p) => ({ ...p, description: e.target.value }))}
                                        placeholder={t("n_a")}
                                        rows={4}
                                    />
                                </div>
                            </div>



                            <section className={styles.telegramConnectCard}>
                                <div className={styles.telegramConnectHeader}>
                                    <div className={styles.telegramConnectTitleWrap}>
                                        <div className={styles.telegramConnectIcon}>
                                            <i className="lni lni-telegram"></i>
                                        </div>
                                        <div className={styles.telegramConnectHeading}>
                                            <div className={styles.telegramConnectEyebrow}>{t("account_internal_sales_workflow")}</div>
                                            <div className={styles.telegramConnectTitle}>{t("account_telegram_crm_link")}</div>
                                            <div className={styles.telegramConnectSubtitle}>
                                                {t("account_telegram_link_description")}
                                            </div>
                                        </div>
                                    </div>

                                    <div
                                        className={`${styles.telegramStatusPill} ${
                                            telegramLink?.data?.linked ? styles.telegramStatusLinked : styles.telegramStatusNotLinked
                                        }`}
                                    >
                                        <span className={styles.telegramStatusDot} />
                                        {telegramLink?.data?.linked ? "Connected" : "Not connected"}
                                    </div>
                                </div>

                                <div className={styles.telegramConnectBody}>
                                    <div className={styles.telegramMainPanel}>
                                        {telegramLink?.data?.linked ? (
                                            <div className={styles.telegramLinkedBox}>
                                                <div className={styles.telegramLinkedAvatar}>
                                                    {(telegramLink.data.telegram_first_name || telegramLink.data.telegram_username || "T")
                                                        .slice(0, 1)
                                                        .toUpperCase()}
                                                </div>
                                                <div className={styles.telegramLinkedMeta}>
                                                    <div className={styles.telegramLinkedTitle}>{t("account_connected")}</div>
                                                    <div className={styles.telegramLinkedText}>
                                                        @{telegramLink.data.telegram_username || "telegram"}
                                                        <span> · </span>
                                                        {t("id")} {telegramLink.data.telegram_user_id}
                                                    </div>
                                                    {telegramLink.data.linked_at && (
                                                        <div className={styles.telegramLinkedDate}>{t("account_linked_at")} {String(telegramLink.data.linked_at)}</div>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className={styles.telegramEmptyState}>
                                                <div className={styles.telegramEmptyIcon}>↗</div>
                                                <div>
                                                    <div className={styles.telegramLinkedTitle}>{t("account_connect_telegram_seconds")}</div>
                                                    <div className={styles.telegramLinkedText}>
                                                        {t("account_connect_telegram_hint")}
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {telegramLink.code && !telegramLink?.data?.linked && (
                                            <div className={styles.telegramCodeGrid}>
                                                <div className={styles.telegramCopyRow}>
                                                    <div className={styles.telegramCopyText}>
                                                        <span>{t("account_code")}</span>
                                                        <strong>{telegramLink.code}</strong>
                                                    </div>
                                                    <button type="button" className={styles.telegramCopyBtn} onClick={copyTelegramCode} disabled={telegramLink.loading}>
                                                        {t("copy")}
                                                    </button>
                                                </div>

                                                <div className={styles.telegramCopyRow}>
                                                    <div className={styles.telegramCopyText}>
                                                        <span>{t("account_command")}</span>
                                                        <code>{telegramManualCommand}</code>
                                                    </div>
                                                    <button type="button" className={styles.telegramCopyBtn} onClick={copyTelegramCommand} disabled={telegramLink.loading}>
                                                        {t("copy")}
                                                    </button>
                                                </div>

                                                {telegramExpiresText && (
                                                    <div className={styles.telegramExpiry}>{t("account_expires")} {telegramExpiresText}</div>
                                                )}
                                            </div>
                                        )}

                                        {telegramLink.success && <div className={styles.telegramAlertSuccess}>{telegramLink.success}</div>}
                                        {telegramLink.error && <div className={styles.telegramAlertError}>{telegramLink.error}</div>}
                                    </div>

                                    <div className={styles.telegramStepsPanel}>
                                        <div className={styles.telegramStepTitle}>{t("landing_watch_how_it_works")}</div>
                                        <div className={styles.telegramSteps}>
                                            <div className={`${styles.telegramStep} ${styles.telegramStepDone}`}>
                                                <span className={styles.telegramStepNumber}>1</span>
                                                <div>
                                                    <b>{t("account_crm_creates_code")}</b>
                                                    <span>{t("account_temporary_code_hint")}</span>
                                                </div>
                                            </div>
                                            <div className={`${styles.telegramStep} ${telegramLink.code || telegramLink?.data?.linked ? styles.telegramStepDone : ""}`}>
                                                <span className={styles.telegramStepNumber}>2</span>
                                                <div>
                                                    <b>{t("account_open_telegram")}</b>
                                                    <span>{t("account_bot_receives_link_code")}</span>
                                                </div>
                                            </div>
                                            <div className={`${styles.telegramStep} ${telegramLink?.data?.linked ? styles.telegramStepDone : ""}`}>
                                                <span className={styles.telegramStepNumber}>3</span>
                                                <div>
                                                    <b>{t("account_take_leads")}</b>
                                                    <span>{t("account_take_lead_chat_hint")}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className={styles.telegramConnectActions}>
                                    {!telegramLink?.data?.linked && (
                                        <button
                                            type="button"
                                            className={styles.telegramPrimaryAction}
                                            onClick={generateTelegramLinkCode}
                                            disabled={telegramLink.loading}
                                        >
                                            {telegramLink.loading ? "Generating..." : telegramLink.code ? "Generate new code" : "Generate link code"}
                                        </button>
                                    )}

                                    {telegramLink.telegram_deep_link && !telegramLink?.data?.linked && (
                                        <a
                                            className={styles.telegramOpenAction}
                                            href={telegramLink.telegram_deep_link}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            <i className="lni lni-telegram"></i>
                                            {t("account_open_telegram_and_link")}
                                        </a>
                                    )}

                                    {telegramLink.code && !telegramLink?.data?.linked && (
                                        <button type="button" className={styles.telegramSecondaryAction} onClick={copyTelegramCommand} disabled={telegramLink.loading}>
                                            {t("account_copy_link_command")}
                                        </button>
                                    )}

                                    <button
                                        type="button"
                                        className={styles.telegramSecondaryAction}
                                        onClick={fetchTelegramLink}
                                        disabled={telegramLink.loading}
                                    >
                                        {t("google_drive_refresh_status")}
                                    </button>

                                    {telegramLink?.data?.linked && (
                                        <button
                                            type="button"
                                            className={styles.telegramDangerAction}
                                            onClick={unlinkTelegramAccount}
                                            disabled={telegramLink.loading}
                                        >
                                            {t("account_unlink_telegram")}
                                        </button>
                                    )}
                                </div>
                            </section>

                            <div className={styles.inlineNote}>
                                <span className={`${styles.badge} ${styles.badge_neutral}`}>{t("account_inline_security")}</span>
                                <span>{t("account_inline_security_hint")}</span>
                            </div>
                        </div>
                    )}

                    {/* EMAIL TAB */}
                    {tab === "email" && !hideEmailTab && (
                        <div className={styles.bigCard}>
                            <div className={styles.bigHeader}>
                                <div>
                                    <div className={styles.bigTitle}>{t("account_email_card_title")}</div>
                                    <div className={styles.bigSub}>{t("account_email_card_sub")}</div>
                                </div>

                                <div className={styles.headerActions}>
                                    <span className={`${styles.badge} ${form.is_active ? styles.badge_success : styles.badge_neutral}`}>
                                        {emailAccountStatusLabel}
                                    </span>

                                    <button type="button" className={styles.secondaryBtnSmall} onClick={fetchMyEmailAccount} disabled={saving}>
                                        {t("account_refresh")}
                                    </button>

                                    <button
                                        type="button"
                                        className={styles.warningBtnSmall}
                                        onClick={onToggleEmailAccountActive}
                                        disabled={saving || loading}
                                    >
                                        {form.is_active ? t("account_disable") : t("account_enable")}
                                    </button>
                                </div>
                            </div>

                            <div className={styles.bigBody}>
                                {notConfigured && <div className="alert alert-info">{t("account_not_configured_info")}</div>}

                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label">{t("account_provider")}</label>
                                        <select
                                            className="form-select"
                                            value={form.provider || "smtp"}
                                            onChange={(e) => onChangeTop("provider", e.target.value)}
                                            disabled={saving}
                                        >
                                            <option value="smtp">smtp</option>
                                        </select>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">{t("account_email_address")}</label>
                                        <input
                                            className="form-control"
                                            value={form.email_address || ""}
                                            onChange={(e) => onChangeTop("email_address", e.target.value)}
                                            disabled={saving}
                                            placeholder="me@company.com"
                                        />
                                    </div>

                                    <div className="col-md-8">
                                        <label className="form-label">{t("account_smtp_host")}</label>
                                        <input
                                            className="form-control"
                                            value={form.smtp_json?.host || ""}
                                            onChange={(e) => onChangeSmtp("host", e.target.value)}
                                            disabled={saving}
                                            placeholder="smtp.gmail.com"
                                        />
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label">{t("account_smtp_port")}</label>
                                        <input
                                            className="form-control"
                                            value={form.smtp_json?.port ?? 587}
                                            onChange={(e) => onChangeSmtp("port", e.target.value)}
                                            disabled={saving}
                                            placeholder="587"
                                        />
                                    </div>

                                    <div className="col-md-8">
                                        <label className="form-label">{t("account_smtp_user")}</label>
                                        <input
                                            className="form-control"
                                            value={form.smtp_json?.user || ""}
                                            onChange={(e) => onChangeSmtp("user", e.target.value)}
                                            disabled={saving}
                                            placeholder="user/login"
                                        />
                                    </div>

                                    <div className="col-md-4 d-flex align-items-end">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                checked={!!form.smtp_json?.tls}
                                                onChange={(e) => onChangeSmtp("tls", e.target.checked)}
                                                disabled={saving}
                                                id="smtp-tls"
                                            />
                                            <label className="form-check-label" htmlFor="smtp-tls">
                                                {t("account_tls")}
                                            </label>
                                        </div>
                                    </div>

                                    <div className="col-md-8">
                                        <label className="form-label">{t("account_smtp_password")}</label>
                                        <input
                                            className="form-control"
                                            type="password"
                                            value={form.smtp_json?.password || ""}
                                            onChange={(e) => onChangeSmtp("password", e.target.value)}
                                            disabled={saving}
                                            placeholder={PASSWORD_MASK}
                                            autoComplete="new-password"
                                            name="smtp-password"
                                        />
                                        <div className="form-text">{t("account_password_mask_hint")}</div>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label">{t("account_from_name_optional")}</label>
                                        <input
                                            className="form-control"
                                            value={form.smtp_json?.from_name || ""}
                                            onChange={(e) => onChangeSmtp("from_name", e.target.value)}
                                            disabled={saving}
                                            placeholder="John Doe"
                                        />
                                    </div>

                                    <div className="col-12 d-flex justify-content-end">
                                        <button type="button" className={styles.primaryBtn} onClick={onSaveEmailAccount} disabled={saving || loading}>
                                            {saving ? t("account_saving") : t("account_save")}
                                        </button>
                                    </div>
                                </div>

                                <div className={styles.note}>• {getEmailModeHint(crmEmail.mode)}</div>
                            </div>
                        </div>
                    )}

                    {/* PASSWORD TAB */}
                    {tab === "password" && (
                        <div className={styles.bigCard}>
                            <div className={styles.bigHeader}>
                                <div>
                                    <div className={styles.bigTitle}>{t("account_password_title")}</div>
                                    <div className={styles.bigSub}>{t("account_password_sub")}</div>
                                </div>
                            </div>

                            <div className={styles.bigBody}>
                                {pwdSuccess && <div className="alert alert-success">{pwdSuccess}</div>}
                                {pwdError && <div className="alert alert-danger">{pwdError}</div>}

                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label">{t("account_current_password_if_required")}</label>
                                        <input
                                            key={`cp-${pwdKey}`}
                                            className="form-control"
                                            type="password"
                                            value={pwdForm.current_password}
                                            onChange={(e) => setPwdForm((p) => ({ ...p, current_password: e.target.value }))}
                                            placeholder={t("account_current_password_placeholder")}
                                            disabled={pwdSaving}
                                            autoComplete="current-password"
                                            name="current-password"
                                        />
                                        <div className="form-text">{t("account_current_password_hint")}</div>
                                    </div>

                                    <div className="col-md-6" />

                                    <div className="col-md-6">
                                        <label className="form-label">{t("account_new_password")}</label>
                                        <input
                                            key={`np-${pwdKey}`}
                                            className="form-control"
                                            type="password"
                                            value={pwdForm.new_password}
                                            onChange={(e) => setPwdForm((p) => ({ ...p, new_password: e.target.value }))}
                                            placeholder={t("account_new_password_placeholder")}
                                            disabled={pwdSaving}
                                            autoComplete="new-password"
                                            name="new-password"
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">{t("account_confirm_new_password")}</label>
                                        <input
                                            key={`cnp-${pwdKey}`}
                                            className="form-control"
                                            type="password"
                                            value={pwdForm.confirm_password}
                                            onChange={(e) => setPwdForm((p) => ({ ...p, confirm_password: e.target.value }))}
                                            placeholder={t("account_confirm_new_password_placeholder")}
                                            disabled={pwdSaving}
                                            autoComplete="new-password"
                                            name="confirm-new-password"
                                        />
                                    </div>

                                    <div className="col-12 d-flex justify-content-end">
                                        <button type="button" className={styles.primaryBtn} onClick={onUpdatePassword} disabled={pwdSaving}>
                                            {pwdSaving ? t("account_saving") : t("account_update_password")}
                                        </button>
                                    </div>
                                </div>

                                <div className={styles.note}>• {t("account_endpoint_fallback")}</div>
                            </div>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
}

export default AccountPage;
